import { Context } from "svgcanvas";
import { canv } from "@/canv/global";
import { sortByIndexByParent } from "@/canvasController/helpers";
import type { Group } from "@/group";
import { prepareAllImagesForExport, queryAreaForExport } from "../utils";
import { embedFontsForSvg } from "../VectorExport/fontUtils";
import { prepareSvgForExport } from "../VectorExport/utils";
import { MAX_EXPORT_SCALE, MAX_EXPORT_SIDE } from "./constants";
import type { ImageExportArea } from "./ImageExportScreen";
import { EXPORT_FORMAT_MIME, type ImageExportFormat, type RasterImageExportFormat } from "./types";

export class ImageExport {
    async exportArea(area: ImageExportArea, format: ImageExportFormat = "png") {
        const normalizedArea = this.normalizeArea(area);
        const groupsForExport = queryAreaForExport(normalizedArea) ?? [];

        sortByIndexByParent(groupsForExport);
        await prepareAllImagesForExport(groupsForExport);

        const snapshot =
            format === "svg"
                ? await this.renderAreaToSvgBlob(normalizedArea, groupsForExport)
                : await this.renderAreaToRasterBlob(normalizedArea, groupsForExport, format);

        this.downloadBlob(snapshot, format);
    }

    private async renderAreaToRasterBlob(
        area: ImageExportArea,
        groupsForExport: Group[],
        format: RasterImageExportFormat,
    ) {
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };
        const appCanvas = canv.canvas;
        const appCtx = canv.ctx;

        const renderScale = this.getRenderScale(area);
        const newCanvas = document.createElement("canvas");
        newCanvas.classList.add("exportCanvas");
        document.body.prepend(newCanvas);

        newCanvas.width = Math.max(1, Math.round(area.width * renderScale));
        newCanvas.height = Math.max(1, Math.round(area.height * renderScale));
        newCanvas.style.width = `${newCanvas.width / canv.dpr}px`;
        newCanvas.style.height = `${newCanvas.height / canv.dpr}px`;

        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) {
            newCanvas.remove();
            throw new Error(
                `Не удалось создать canvas контекст для экспорта ${format.toUpperCase()}`,
            );
        }

        try {
            canv.canvas = newCanvas;
            canv.ctx = newCtx;
            canv.m[0] = renderScale;
            canv.m[3] = renderScale;
            canv.m[4] = -area.startX * canv.m[0];
            canv.m[5] = -area.startY * canv.m[3];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);

            canv.redraw();
            canv.redraw(true, true, groupsForExport);

            const snapshot = await new Promise<Blob>((resolve, reject) => {
                newCanvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error(`Не удалось создать ${format.toUpperCase()} blob`));
                            return;
                        }

                        resolve(blob);
                    },
                    EXPORT_FORMAT_MIME[format],
                    1,
                );
            });

            return snapshot;
        } finally {
            newCanvas.remove();
            canv.canvas = appCanvas;
            canv.ctx = appCtx;

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.redraw();
        }
    }

    private async renderAreaToSvgBlob(area: ImageExportArea, groupsForExport: Group[]) {
        canv.setIsHighPdfExporting(true);

        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };
        const appCanvas = canv.canvas;
        const appCtx = canv.ctx;
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = Math.max(1, Math.round(area.width));
        exportCanvas.height = Math.max(1, Math.round(area.height));

        try {
            const svgCanvas = new Context({
                height: area.height,
                width: area.width,
                ctx: appCtx,
                enableMirroring: false,
            });

            canv.canvas = exportCanvas;
            canv.ctx = svgCanvas;
            canv.m[0] = 1;
            canv.m[3] = 1;
            canv.m[4] = -area.startX;
            canv.m[5] = -area.startY;
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);

            canv.redraw();
            canv.redraw(true, true, groupsForExport);

            const svgString = svgCanvas.getSerializedSvg();
            const svgElement = new DOMParser().parseFromString(
                svgString,
                "image/svg+xml",
            ).documentElement;

            svgElement.setAttribute("viewBox", `0 0 ${area.width} ${area.height}`);
            svgElement.setAttribute("width", `${area.width}px`);
            svgElement.setAttribute("height", `${area.height}px`);
            svgElement.setAttribute("overflow", "hidden");

            await prepareSvgForExport(svgElement);
            await embedFontsForSvg(svgElement);

            const serializedSvg = new XMLSerializer().serializeToString(svgElement);

            return new Blob([serializedSvg], {
                type: "image/svg+xml;charset=utf-8",
            });
        } finally {
            canv.canvas = appCanvas;
            canv.ctx = appCtx;

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.setIsHighPdfExporting(false);
            canv.redraw();
        }
    }

    private normalizeArea(area: ImageExportArea): ImageExportArea {
        return {
            startX: area.width >= 0 ? area.startX : area.startX + area.width,
            startY: area.height >= 0 ? area.startY : area.startY + area.height,
            width: Math.max(1, Math.abs(area.width)),
            height: Math.max(1, Math.abs(area.height)),
        };
    }

    private getRenderScale(area: ImageExportArea) {
        const baseScale = canv.dpr;
        const scale = Math.min(
            MAX_EXPORT_SCALE,
            MAX_EXPORT_SIDE / (area.width * baseScale),
            MAX_EXPORT_SIDE / (area.height * baseScale),
        );

        return baseScale * Math.max(Number.EPSILON, scale);
    }

    private downloadBlob(blob: Blob, format: ImageExportFormat) {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${this.getFileName()}.${format}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(objectUrl);
    }

    private getFileName() {
        return `board-export-${Date.now()}`;
    }
}
